import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "acta-ds-btn-default bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "acta-ds-btn-destructive bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "acta-ds-btn-outline border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "acta-ds-btn-secondary bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "acta-ds-btn-ghost hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",

        // Enterprise Premium variants are additive so legacy screens keep
        // their current rendering until they are migrated page by page.
        actaPrimary:
          "acta-ds-btn-primary rounded-[10px] border border-[var(--do-accent)] bg-[var(--do-accent)] text-white shadow-none hover:bg-[var(--do-accent-strong)]",
        actaSecondary:
          "acta-ds-btn-secondary rounded-[10px] border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] shadow-none hover:bg-[var(--do-bg-2)]",
        actaGhost:
          "acta-ds-btn-ghost rounded-[10px] border border-transparent bg-transparent text-[var(--do-ink)] shadow-none hover:bg-[var(--do-bg-2)]",
        actaDanger:
          "acta-ds-btn-danger rounded-[10px] border border-[var(--do-rose)] bg-[var(--do-rose)] text-white shadow-none hover:opacity-90",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
